<template>
  <v-modal id="pax-update-modal" class-name="modal-fullscreen-sm-down" size="lg" @closed="$emit('closed')">
    <modal-header>
      <modal-title>
        {{ $t("clientTour.updatePax") }}
      </modal-title>
    </modal-header>

    <modal-body>
      <form id="case-client-update-form" class="d-flex flex-column justify-content-between h-100">
        <fieldset>
          <legend class="visually-hidden">{{ $t("client.basicInformation") }}</legend>

          <v-form-input
            id="case-pax-update-name"
            v-model="name"
            :label="`${$t('general.shared.name')}*`"
            :yup-errors-variable="errors.name"
            autocomplete="off"
            class="mb-8"
            form-type="outline"
          />

          <v-form-input
            id="case-pax-update-email"
            v-model="email"
            :label="`${$t('general.shared.email')}*`"
            :yup-errors-variable="errors.email"
            autocomplete="off"
            class="mb-8"
            form-type="outline"
            type="email"
          />

          <v-form-input
            id="case-pax-update-phone"
            v-model="phone"
            :label="$t('general.shared.phone')+ '*'"
            :yup-errors-variable="errors.phone"
            autocomplete="off"
            class="mb-8"
            form-type="outline"
          />
        </fieldset>

        <v-button
          :disabled="!meta.valid || accepted"
          :is-loading="accepted"
          class="btn-submit-form btn-icon w-fit align-self-end"
          size="sm"
          variant="primary"
          @click="updatePax"
        >
          {{ $t("general.button.save") }}
          <v-icon icon="arrow-right" size="sm" space="ms-12" />
        </v-button>
      </form>
    </modal-body>
  </v-modal>
</template>

<script lang="ts" setup name="ThePaxInfoEditModal">
import { nextTick, watch } from "vue";
import { useField } from "vee-validate";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { VFormInput } from "@uniqoders/form";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalTitle from "@/components/vendor/basic/modal/ModalTitle.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import useFormValidation from "@/helpers/form";
import api from "@/api";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useToast } from "vue-toastification";

const props = defineProps<{
  agency: string;
  caseId?: number | null;
  pax: any;
}>();

const emit = defineEmits(["closed", "reloadPax"]);

const { t } = useI18n();
const toast = useToast();

const rules = yup.object({
  name: yup.string().required().label(t("general.shared.name")),
  email: yup.string().email().required().label(t("general.shared.email")),
  phone: yup.string().required().label(t('general.shared.phone')),
});

const { errors, meta, accepted } = useFormValidation(rules);

const { value: name } = useField<string>("name");
const { value: email } = useField<string>("email");
const { value: phone } = useField<string>("phone");

watch(
  () => props.pax,
  async (newPax) => {
    if (newPax) {
      name.value = newPax?.name || "";
      email.value = newPax?.email || "";
      phone.value = newPax?.phone || "";

      await nextTick();
    }
  },
  { immediate: true }
);

const updatePax = async () => {

  const payload = {
    name: name.value,
    email: email.value,
    phone: phone.value,
  };

  try {
    await api.case.updatePax(props.agency, props.pax?.id, payload);
    toast.success(t("general.shared.savedChanges"));
    emit("reloadPax");
    emit("closed");
  } catch (error: any) {
    console.log("error: ", error);
    toast.error(error?.response?.data?.message);
  }
};
</script>
